import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Buildings, Plus, Users, ChartBar, CrownSimple, GraduationCap, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="grid h-10 w-10 place-items-center rounded-xl" style={{ background: `${tone}1a`, color: tone }}><Icon size={22} weight="duotone" /></div>
      <div className="mt-3 font-display text-2xl font-extrabold">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

export default function SchoolSpace() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [newClass, setNewClass] = useState({ name: "", exam_id: "" });
  const [createOpen, setCreateOpen] = useState(false);
  const [addEmail, setAddEmail] = useState({});

  const load = () => {
    api.get("/school/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/school/classes").then((r) => setClasses(r.data)).catch(() => {});
  };
  useEffect(() => { api.get("/exams").then((r) => setExams(r.data)); load(); }, []);

  const createClass = async () => {
    if (!newClass.name || !newClass.exam_id) { toast.error("Nom et examen requis."); return; }
    try {
      await api.post("/school/classes", newClass);
      toast.success("Classe créée !");
      setNewClass({ name: "", exam_id: "" });
      setCreateOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addStudent = async (classId) => {
    const email = addEmail[classId];
    if (!email) { toast.error("Saisissez un email."); return; }
    try {
      await api.post(`/school/classes/${classId}/add-student`, { email });
      toast.success("Élève ajouté !");
      setAddEmail({ ...addEmail, [classId]: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3 animate-fade-up">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Espace établissement</span>
          <h1 className="mt-2 flex items-center gap-2 font-display text-3xl font-extrabold sm:text-4xl"><Buildings weight="fill" className="text-forest" /> {user?.name}</h1>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-class-btn" className="rounded-full bg-terracotta text-white hover:bg-[#E03E00]"><Plus weight="bold" className="mr-1.5" /> Nouvelle classe</Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-display">Créer une classe</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="class-name" value={newClass.name} onChange={(e) => setNewClass({ ...newClass, name: e.target.value })} placeholder="Nom (ex : Terminale D1)" className="rounded-xl" />
              <select data-testid="class-exam" value={newClass.exam_id} onChange={(e) => setNewClass({ ...newClass, exam_id: e.target.value })} className="w-full rounded-xl border border-border bg-white px-3 py-2.5 text-sm">
                <option value="">Examen préparé…</option>
                {exams.map((e) => <option key={e.id} value={e.id}>{e.name} — {e.classe}</option>)}
              </select>
              <Button data-testid="confirm-create-class" onClick={createClass} className="w-full rounded-full bg-terracotta text-white hover:bg-[#E03E00]">Créer la classe</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={GraduationCap} label="Classes" value={stats?.classes ?? 0} tone="#FF4B0A" />
        <Stat icon={Users} label="Élèves" value={stats?.students ?? 0} tone="#042A65" />
        <Stat icon={ChartBar} label="Moyenne globale" value={`${stats?.global_avg ?? 0}%`} tone="#355070" />
        <Stat icon={CrownSimple} label="Abonnés actifs" value={stats?.active_subscriptions ?? 0} tone="#FFB800" />
      </div>

      {classes.length ? classes.map((c) => (
        <div key={c.id} data-testid={`class-${c.id}`} className="rounded-2xl border border-border bg-white p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-display text-lg font-bold">{c.name}</div>
              <div className="text-sm text-muted-foreground">{c.exam?.name || "—"} · {c.student_count} élève(s) · moyenne {c.class_avg}% · code <span className="font-mono text-forest">{c.join_code}</span></div>
            </div>
            <div className="flex gap-2">
              <Input data-testid={`add-email-${c.id}`} value={addEmail[c.id] || ""} onChange={(e) => setAddEmail({ ...addEmail, [c.id]: e.target.value })} placeholder="email élève" className="w-48 rounded-xl" />
              <Button data-testid={`add-student-${c.id}`} onClick={() => addStudent(c.id)} variant="outline" className="rounded-full"><UserPlus weight="bold" className="mr-1" /> Ajouter</Button>
            </div>
          </div>
          {c.students.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Élève</TableHead><TableHead>Email</TableHead><TableHead>Quiz</TableHead><TableHead>Moyenne</TableHead><TableHead>XP</TableHead></TableRow></TableHeader>
                <TableBody>
                  {c.students.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-muted-foreground">{s.email}</TableCell>
                      <TableCell>{s.quizzes_done}</TableCell>
                      <TableCell><span className={s.avg_score >= 50 ? "text-forest" : "text-destructive"}>{s.avg_score}%</span></TableCell>
                      <TableCell>{s.xp}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )) : (
        <div className="rounded-2xl border border-dashed border-border bg-white p-10 text-center">
          <Buildings weight="duotone" className="mx-auto text-terracotta" size={32} />
          <p className="mt-2 text-sm text-muted-foreground">Aucune classe. Créez votre première classe pour suivre vos élèves et activer les licences groupées.</p>
        </div>
      )}
    </div>
  );
}
